import React, { useState, useCallback, useEffect } from 'react';
import { manufacturingAPI } from '../../../services/api';
import './ItemSearchPanel.css';

const ItemSearchPanel = ({ onLoadBOM, onCreateItem, topLevelItem, pendingItemCodes = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        searchItems();
      } else if (searchTerm.length === 0) {
        loadRecentItems();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, filterType]);

  const searchItems = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchTerm,
        page_size: 50,
      };
      
      if (filterType !== 'all') {
        params.item_type = filterType;
      }

      const response = await manufacturingAPI.items.list(params);
      setItems(response.data.results || response.data);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentItems = async () => {
    setLoading(true);
    try {
      const params = {
        page_size: 20,
        ordering: '-updated_at',
      };
      
      if (filterType !== 'all') {
        params.item_type = filterType;
      }

      const response = await manufacturingAPI.items.list(params);
      setItems(response.data.results || response.data);
    } catch (err) {
      console.error('Load recent failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = useCallback((event, item) => {
    event.dataTransfer.setData('application/bom-item', JSON.stringify(item));
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  const getTypeColor = (type, isPhantom, isPending) => {
    if (isPending) return '#8b5cf6'; // Purple for pending
    if (isPhantom) return '#f59e0b';
    switch (type) {
      case 'finished_good': return '#22c55e';
      case 'assembly': return '#3b82f6';
      case 'manufactured': return '#8b5cf6';
      case 'raw_material': return '#64748b';
      default: return '#94a3b8';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'finished_good': return 'FG';
      case 'assembly': return 'ASM';
      case 'manufactured': return 'MFG';
      case 'raw_material': return 'RAW';
      default: return type?.substring(0, 3).toUpperCase() || 'UNK';
    }
  };

  return (
    <div className="item-search-panel">
      {/* Header */}
      <div className="panel-header">
        <h2>Items</h2>
        <div className="header-buttons">
          <button 
            className="btn-create-item"
            onClick={onCreateItem}
            title="Create a new item"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New
          </button>
          <button 
            className="btn-load-bom"
            onClick={onLoadBOM}
            title="Load an existing BOM onto the canvas"
          >
            Load BOM
          </button>
        </div>
      </div>

      {/* Current Top-Level Item Display */}
      {topLevelItem && (
        <div className="current-top-level">
          <span className="top-level-label">Building:</span>
          <span className="top-level-code">{topLevelItem.itemCode}</span>
          {topLevelItem.isPending && <span className="pending-tag">NEW</span>}
        </div>
      )}

      {/* Pending Items Count */}
      {pendingItemCodes.length > 0 && (
        <div className="pending-items-info">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{pendingItemCodes.length} new item(s) to be created on save</span>
        </div>
      )}

      {/* Search Input */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button 
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
          title="Filter by type"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
        </button>
      </div>

      {/* Filter Options */}
      {showFilters && (
        <div className="filter-options">
          <button 
            className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            All
          </button>
          <button 
            className={`filter-btn ${filterType === 'finished_good' ? 'active' : ''}`}
            onClick={() => setFilterType('finished_good')}
            style={{ borderColor: '#22c55e' }}
          >
            Finished
          </button>
          <button 
            className={`filter-btn ${filterType === 'assembly' ? 'active' : ''}`}
            onClick={() => setFilterType('assembly')}
            style={{ borderColor: '#3b82f6' }}
          >
            Assembly
          </button>
          <button 
            className={`filter-btn ${filterType === 'manufactured' ? 'active' : ''}`}
            onClick={() => setFilterType('manufactured')}
            style={{ borderColor: '#8b5cf6' }}
          >
            Manufactured
          </button>
          <button 
            className={`filter-btn ${filterType === 'raw_material' ? 'active' : ''}`}
            onClick={() => setFilterType('raw_material')}
            style={{ borderColor: '#64748b' }}
          >
            Raw
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="drag-instructions">
        Drag items onto the canvas to add them
      </div>

      {/* Items List */}
      <div className="items-list">
        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            Searching...
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="empty-state">
            {searchTerm ? (
              <>
                <p>No items found</p>
                <button 
                  className="btn-create-from-search"
                  onClick={onCreateItem}
                >
                  Create "{searchTerm}" as new item
                </button>
              </>
            ) : (
              'Type to search items'
            )}
          </div>
        )}

        {!loading && items.map((item) => {
          const isOnCanvas = topLevelItem?.itemCode === item.item_code;
          
          return (
            <div
              key={item.id}
              className={`item-card ${item.is_phantom ? 'phantom' : ''} ${isOnCanvas ? 'is-on-canvas' : ''} ${item.isPending ? 'is-pending' : ''}`}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, item)}
            >
              <div 
                className="type-indicator"
                style={{ backgroundColor: getTypeColor(item.item_type, item.is_phantom, item.isPending) }}
                title={item.item_type}
              >
                {item.isPending ? 'NEW' : item.is_phantom ? 'P' : getTypeLabel(item.item_type)}
              </div>
              <div className="item-info">
                <div className="item-code">
                  {item.item_code}
                  {isOnCanvas && <span className="on-canvas-tag">ON CANVAS</span>}
                  {item.isPending && <span className="pending-tag">UNSAVED</span>}
                </div>
                <div className="item-name">{item.name}</div>
              </div>
              <div className="item-actions">
                <div className="drag-handle">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="9" cy="5" r="2"/>
                    <circle cx="15" cy="5" r="2"/>
                    <circle cx="9" cy="12" r="2"/>
                    <circle cx="15" cy="12" r="2"/>
                    <circle cx="9" cy="19" r="2"/>
                    <circle cx="15" cy="19" r="2"/>
                  </svg>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="legend">
        <div className="legend-title">Legend</div>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#22c55e' }}></span>
            Finished Good
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#3b82f6' }}></span>
            Assembly
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#8b5cf6' }}></span>
            Manufactured
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#64748b' }}></span>
            Raw Material
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#f59e0b' }}></span>
            Phantom
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemSearchPanel;